import { Button } from '@components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@components/ui/dialog';
import { SearchableSelect } from '@components/ui/searchable-select';
import { Textarea } from '@components/ui/textarea';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useCaseRecordCalls, useUpdateCall } from '@/hooks/useCalls';
import { useDispositions, useSelectableDispositions } from '@/hooks/useDispositions';
import type { ActionDataParams } from '@/types/actions';
import type { SelectOption } from '@/types/formSchema';
import { compareDispositionLabels, getDispositionsForWorkflow } from '@/utils/dispositions';

interface ActionCallReviewDialogProps {
  isOpen: boolean;
  onClose: () => void;
  caseRecordId: string;
  action: string;
  onActionSelect: (action: string, caseId: string, actionDataParams?: ActionDataParams) => void;
  workflowType?: string;
  title?: string;
  description?: string;
  inputLabel?: string;
  inputPlaceholder?: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
}

export function ActionCallReviewDialog({
  isOpen,
  onClose,
  caseRecordId,
  action,
  onActionSelect,
  workflowType,
  title = 'Before we move on',
  description,
  inputLabel = 'Last call notes to be updated on Patient Chart',
  inputPlaceholder = 'Enter call notes...',
  confirmButtonText = 'Save call notes',
  cancelButtonText = 'Cancel',
}: ActionCallReviewDialogProps) {
  const [inputValue, setInputValue] = useState('');
  const [selectedDisposition, setSelectedDisposition] = useState<string | number>('');

  const updateCallMutation = useUpdateCall();

  // Fetch calls for this case record to get the last call (only when dialog is open)
  const { data: callsData } = useCaseRecordCalls(isOpen ? caseRecordId : null);
  // Find the most recently started call by started_at timestamp
  // (not relying on array sort order which uses scheduled_at).
  //
  // Calls this case was only COVERED by are skipped. Stacking writes a shell
  // call onto each covered case, started at the instant the stack was asserted
  // — later than the call that was actually placed — so it would always win
  // this reduce and take the disposition and reachout note meant for the real
  // call. A covered call is identified by a call_stack whose stack_id is some
  // OTHER call: the placed call self-links, so its stack_id is its own id.
  const lastCall = useMemo(() => {
    const calls = callsData?.calls?.filter(
      call => !call.call_stack?.stack_id || call.call_stack.stack_id === call.id,
    );
    if (!calls || calls.length === 0) return null;
    return calls.reduce((latest, call) => {
      if (!call.started_at) return latest;
      if (!latest?.started_at) return call;
      return new Date(call.started_at) > new Date(latest.started_at) ? call : latest;
    }, calls[0]);
  }, [callsData]);
  const lastCallId = lastCall?.id ?? null;

  // Resolve dispositions from the @100mslive/disposition-engine package
  const { data: apiDispositions = [], isLoading: isLoadingDispositions } = useDispositions(
    isOpen ? workflowType : undefined,
  );

  // Selectable-only list for the dropdown. `apiDispositions` above stays the FULL
  // catalog because the label lookup below resolves the selected code through it —
  // filtering that array would break read-back for a call already set to a retired
  // code. The current value is kept selectable so the control is not blank.
  const { data: selectableDispositions = [] } = useSelectableDispositions(
    isOpen ? workflowType : undefined,
    lastCall?.disposition_type as number | undefined,
  );

  // Determine if using API or fallback dispositions (same pattern as GlobalDialpad)
  const useApiDispositions = apiDispositions.length > 0;

  const dispositionOptions = useMemo<SelectOption[]>(
    () =>
      useApiDispositions
        ? selectableDispositions
            .filter(d => d.code !== undefined && d.label)
            .map(d => ({ value: d.code!, label: d.label! }))
        : [...getDispositionsForWorkflow(workflowType)]
            .sort(compareDispositionLabels)
            .map(label => ({ value: label, label })),
    [selectableDispositions, useApiDispositions, workflowType],
  );

  const isSaving = updateCallMutation.isPending;
  const isConfirmDisabled = !inputValue.trim() || !selectedDisposition || !lastCallId || isSaving;

  const handleConfirm = useCallback(
    async (e?: React.MouseEvent) => {
      e?.stopPropagation();
      e?.preventDefault();
      if (!inputValue.trim() || !selectedDisposition || !lastCallId) {
        return;
      }

      // Build disposition label from selection.
      // When useApiDispositions is false, options have string values (labels),
      // so selectedDisposition is guaranteed to be a string in that branch.
      const dispositionLabel = useApiDispositions
        ? apiDispositions.find(d => d.code === selectedDisposition)?.label || ''
        : (selectedDisposition as string);

      try {
        // Step 1: Update call with disposition and reachout note via updateCall
        await updateCallMutation.mutateAsync({
          caseRecordId,
          callId: lastCallId,
          data: {
            disposition: { type: dispositionLabel },
            ...(useApiDispositions &&
              typeof selectedDisposition === 'number' && {
                disposition_type: selectedDisposition,
              }),
            chart_info: { notes: inputValue.trim() },
          },
        });

        // Step 2: Execute the action (note is already saved on the call via updateCall)
        onClose();
        onActionSelect(action, caseRecordId);
        setInputValue('');
        setSelectedDisposition('');
      } catch (error) {
        const message =
          (error as any)?.response?.data?.message || 'Failed to save call disposition';
        toast.error(message);
      }
    },
    [
      inputValue,
      selectedDisposition,
      lastCallId,
      useApiDispositions,
      apiDispositions,
      caseRecordId,
      action,
      onActionSelect,
      onClose,
      updateCallMutation,
    ],
  );

  const handleClose = useCallback(() => {
    setInputValue('');
    setSelectedDisposition('');
    onClose();
  }, [onClose]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        handleConfirm();
      }
    },
    [handleConfirm],
  );

  // Pre-populate from the last call's existing disposition and reachout note
  useEffect(() => {
    if (!isOpen) return;

    // Pre-fill reachout note from chart_info.notes
    setInputValue(lastCall?.chart_info?.notes ?? '');

    // Pre-fill disposition: use numeric code for API dispositions, label for fallback
    if (useApiDispositions && lastCall?.disposition_type) {
      setSelectedDisposition(lastCall.disposition_type);
    } else if (lastCall?.disposition?.type) {
      setSelectedDisposition(lastCall.disposition.type);
    } else {
      setSelectedDisposition('');
    }
  }, [isOpen, lastCall, useApiDispositions]);

  return (
    <Dialog open={isOpen} onOpenChange={isOpen => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-[500px]" onClick={e => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <label htmlFor="call-review-disposition" className="text-sm font-medium">
              Disposition
              <span className="text-destructive ml-1">*</span>
            </label>
            <SearchableSelect
              id="call-review-disposition"
              value={selectedDisposition}
              onValueChange={value => setSelectedDisposition(value as string | number)}
              options={dispositionOptions}
              placeholder={isLoadingDispositions ? 'Loading...' : 'Select disposition...'}
              disabled={isLoadingDispositions || isSaving}
              searchPlaceholder="Search dispositions..."
              emptyText="No dispositions found"
            />
          </div>
          <div className="grid gap-2">
            <label htmlFor="call-review-input" className="text-sm font-medium">
              {inputLabel}
              <span className="text-destructive ml-1">*</span>
            </label>
            <Textarea
              id="call-review-input"
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={inputPlaceholder}
              className="min-h-[120px]"
              disabled={isSaving}
              autoFocus
            />
            {!inputValue.trim() && (
              <p className="text-sm text-muted-foreground">This field is required</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isSaving}>
            {cancelButtonText}
          </Button>
          <Button onClick={handleConfirm} disabled={isConfirmDisabled}>
            {isSaving ? 'Processing...' : confirmButtonText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
